/**
 * @file src/utils/contextHelper.js
 * @description A helper to generate a context object for API headers.
 */

import { getAppConfig } from '../globalHeader/services/AppConfigService'

/**
 * Enum for platform types to ensure consistency and prevent typos.
 */
export const PLATFORM_TYPES = {
    APP: 'app',
    WEB_DESKTOP: 'web_desktop',
    WEB_MOBILE: 'web_mobile',
}

/**
 * Checks if the app is running inside a native WebView.
 * This is useful for web-to-native wrapper services.
 *
 * @param {string} userAgent - The browser's user agent string.
 * @returns {boolean} True if a WebView environment is detected, false otherwise.
 */

/**
 * Detects the client platform (WebView, Mobile Web, or Desktop Web).
 *
 * This function is safe to call in any environment (client or server).
 * It will return an empty object during Server-Side Rendering (SSR)
 * and the correct platform when executed in a browser.
 *
 * @returns {{platform: string} | {}} An object containing the platform type or an empty object.
 */
const getPlatformContext = () => {
    // Guard clause to prevent errors during SSR (e.g., in Next.js)
    // where `window` and `navigator` are not defined.
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return {}
    }

    const userAgent = navigator.userAgent

    // --- 1. Check for WebView ---
    if (getAppConfig()?.isMobileApp) {
        return { platform: PLATFORM_TYPES.APP }
    }

    // --- 2. Differentiate between Mobile and Desktop Web ---
    // The `userAgentData` API is a modern, reliable way to check for mobile.
    // @ts-ignore
    if (navigator.userAgentData && navigator.userAgentData?.mobile) {
        return { platform: PLATFORM_TYPES.WEB_MOBILE }
    }

    // Fallback for older browsers using a regex on the user agent string.
    if (/Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)) {
        return { platform: PLATFORM_TYPES.WEB_MOBILE }
    }

    // If it's not a WebView and not mobile, it's a desktop browser.
    return { platform: PLATFORM_TYPES.WEB_DESKTOP }
}

/**
 * Creates the complete X-Context object for API requests.
 * This function combines the platform context with other useful client-side info.
 *
 * @param {object} [additionalContext={}] - Any other dynamic data to include.
 * @returns {object} The final context object ready for JSON.stringify.
 */
export const createClientContext = (additionalContext = {}) => {
    const platformContext = getPlatformContext()

    // Return empty object if on server
    if (!platformContext.platform) {
        return {}
    }

    return {
        ...platformContext,
        // timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // locale: navigator.language,
        ...additionalContext,
    }
}
